import React, { useState, useEffect } from 'react';
import { 
  BoltIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { UnifiedMetricCard } from '../../components/ui/UnifiedMetricCard';
import { UnifiedTrendChart } from '../../components/charts/UnifiedTrendChart';
import AlertsTable from '../../components/Tables/AlertsTable';
import { monitoringApi, DashboardOverview, PowerTrend, WeatherData } from '../../services/monitoringApi';

interface OverviewData {
  dashboard: DashboardOverview | null;
  powerTrends: PowerTrend[];
  weather: WeatherData | null;
}

interface Alert {
  id: string;
  timestamp: string;
  equipment: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'active' | 'acknowledged' | 'resolved';
}

const Overview: React.FC = () => {
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [timeRange, setTimeRange] = useState('1d');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchOverviewData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchOverviewData, 60000); // Stagger to 60s to ease backend load
      return () => clearInterval(interval);
    }
  }, [timeRange, autoRefresh]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const hours =
        timeRange === '1d' ? 24 : timeRange === '7d' ? 168 : 720;
      // Fetch sequentially to avoid dogpiling the backend
      const dashboard = await monitoringApi.getDashboardOverview();
      const powerTrends = await monitoringApi.getPowerTrends(hours);
      const weather = await monitoringApi.getWeatherData();
      setOverviewData({ dashboard, powerTrends, weather });

      // Mock alerts for now (can be replaced with real alert API)
      const mockAlerts: Alert[] = [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          equipment: 'ICR_06_INV_01',
          type: 'Performance Drop',
          severity: 'warning',
          status: 'active'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          equipment: 'WMS_0',
          type: 'Temperature Alert',
          severity: 'info',
          status: 'acknowledged'
        }
      ];
      setAlerts(mockAlerts);
      
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    // Export functionality
    window.print();
  };

  const handleExportCSV = () => {
    // CSV export logic
    const csvData = energyTrends.map(trend => 
      `${trend.date},${trend.actual},${trend.predicted}`
    ).join('\n');
    
    const blob = new Blob([`Date,Actual,Predicted\n${csvData}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'energy-trends.csv';
    a.click();
  };

  if (loading && !overviewData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Header */}
      <div className="flex-between flex-wrap gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">
            Solar Intelligence Overview
          </h1>
          <p className="text-emerald-700 dark:text-emerald-300 text-lg font-medium">
            Comprehensive renewable energy monitoring dashboard
          </p>
          <div className="flex items-center gap-4 text-sm text-emerald-600 dark:text-emerald-400">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span>Live Data Stream</span>
            </div>
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4" />
              <span>Updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-xl border font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
              autoRefresh 
                ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300' 
                : 'bg-white border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
            }`}
          >
            <ArrowPathIcon className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Auto Refresh</span>
          </button>
          
          <div className="flex items-center gap-2">
            <button onClick={handleExportPDF} className="px-4 py-2 rounded-xl border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all duration-300 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700">
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Export PDF</span>
            </button>
            
            <button onClick={handleExportCSV} className="px-4 py-2 rounded-xl border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all duration-300 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700">
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Performance Dashboard Cards */}
      <div className="grid-1-4">
        <div className="bg-white dark:bg-emerald-950/30 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-700/50 shadow-sm hover:shadow-lg transition-all duration-300 group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              <BoltIcon className="w-7 h-7 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-900 dark:text-emerald-100">
                {overviewData?.dashboard?.total_power.toFixed(1) || '0.0'}
              </div>
              <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">kW</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Current Power Output</div>
            <div className="text-xs text-emerald-700 dark:text-emerald-300">
              Live generation data
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-lime-950/30 rounded-2xl p-6 border border-lime-200 dark:border-lime-700/50 shadow-sm hover:shadow-lg transition-all duration-300 group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              <CheckCircleIcon className="w-7 h-7 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-lime-900 dark:text-lime-100">
                {overviewData?.dashboard?.equipment_status.total_active || 0}
              </div>
              <div className="text-sm text-lime-600 dark:text-lime-400 font-medium">systems</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-semibold text-lime-900 dark:text-lime-100">Active Systems</div>
            <div className="text-xs text-lime-700 dark:text-lime-300">
              Online & Operational
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-amber-950/30 rounded-2xl p-6 border border-amber-200 dark:border-amber-700/50 shadow-sm hover:shadow-lg transition-all duration-300 group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              <ExclamationTriangleIcon className="w-7 h-7 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-amber-900 dark:text-amber-100">
                {(overviewData?.dashboard?.alerts.critical || 0) + (overviewData?.dashboard?.alerts.warning || 0) + (overviewData?.dashboard?.alerts.info || 0)}
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-400 font-medium">alerts</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">System Alerts</div>
            <div className="text-xs text-amber-700 dark:text-amber-300">
              {((overviewData?.dashboard?.alerts.critical || 0) + (overviewData?.dashboard?.alerts.warning || 0) + (overviewData?.dashboard?.alerts.info || 0)) === 0 ? 'All Systems Optimal' : 'Requires Attention'}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-teal-950/30 rounded-2xl p-6 border border-teal-200 dark:border-teal-700/50 shadow-sm hover:shadow-lg transition-all duration-300 group">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              <ClockIcon className="w-7 h-7 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-teal-900 dark:text-teal-100">
                {overviewData?.dashboard?.equipment_status.inverters || 0}
              </div>
              <div className="text-sm text-teal-600 dark:text-teal-400 font-medium">inverters</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-semibold text-teal-900 dark:text-teal-100">Active Inverters</div>
            <div className="text-xs text-teal-700 dark:text-teal-300">
              Power Generation Units
            </div>
          </div>
        </div>
      </div>

      {/* Energy Trends Chart */}
      <div className="bg-white dark:bg-emerald-950/20 rounded-2xl p-8 border border-emerald-200 dark:border-emerald-700/30 shadow-lg">
        <div className="flex-between mb-8 pb-6 border-b border-emerald-200 dark:border-emerald-700/50">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-emerald-900 dark:text-emerald-100">
              Energy Generation Intelligence
            </h2>
            <p className="text-emerald-700 dark:text-emerald-300 text-sm">
              AI-powered forecasting vs actual output
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 rounded-xl border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100 text-sm min-w-[140px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="1d">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            
            <button 
              onClick={fetchOverviewData}
              className="p-2 rounded-xl border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-800/50 transition-colors"
              disabled={loading}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        <div className="relative">
          <UnifiedTrendChart 
            data={overviewData?.powerTrends || []} 
            lines={[
              { dataKey: 'actual', name: 'Actual Generation', color: '#f97316' },
              { dataKey: 'predicted', name: 'AI Prediction', color: '#0ea5e9', strokeDashArray: '8 4' }
            ]}
            type="area"
            loading={false}
          />
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-white dark:bg-amber-950/20 rounded-2xl p-8 border border-amber-200 dark:border-amber-700/30 shadow-lg">
        <div className="flex-between mb-8 pb-6 border-b border-amber-200 dark:border-amber-700/50">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-amber-900 dark:text-amber-100">
              System Intelligence Alerts
            </h2>
            <p className="text-amber-700 dark:text-amber-300 text-sm">
              Smart monitoring notifications and insights
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
              <div className={`w-2 h-2 rounded-full ${
                alerts.filter(a => a.status === 'active').length > 0 
                  ? 'bg-amber-500 animate-pulse' 
                  : 'bg-emerald-500'
              }`} />
              <span>
                {alerts.filter(a => a.status === 'active').length} active alerts
              </span>
            </div>
            
            <button 
              onClick={fetchOverviewData}
              className="px-3 py-2 rounded-xl border border-amber-300 dark:border-amber-600 bg-white dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-800/50 transition-colors text-sm flex items-center gap-2"
              disabled={loading}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
        
        <div className="relative">
          <AlertsTable 
            alerts={alerts} 
            onAcknowledge={(alertId) => {
              setAlerts(prev => prev.map(alert => 
                alert.id === alertId 
                  ? { ...alert, status: 'acknowledged' as const }
                  : alert
              ));
            }}
            onViewDetails={(alertId) => {
              const alert = alerts.find(a => a.id === alertId);
              if (alert) {
                alert(`Alert Details: ${alert.equipment} - ${alert.type}\nSeverity: ${alert.severity}\nTime: ${alert.timestamp}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Overview;
